import React from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { Image as ImageIcon, Video as VideoIcon, FileText, X, Loader2, Download } from 'lucide-react';

export interface MediaFile {
  name: string;
  type?: string;
  url: string;
}

export interface MediaViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: MediaFile | null;
  isFetchingUrl?: boolean;
  onDownload?: (file: MediaFile) => void;
}

const MediaViewerModal: React.FC<MediaViewerModalProps> = ({
  isOpen,
  onClose,
  file,
  isFetchingUrl = false,
  onDownload,
}) => {
  const isImage = (type?: string) => type?.startsWith('image/') || false;
  const isVideo = (type?: string) => type?.startsWith('video/') || false;
  const isPdf = (type?: string) => type === 'application/pdf';
  const isDoc = (type?: string) =>
    type === 'application/msword' ||
    type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    type?.includes('word') ||
    type?.includes('officedocument');

  // Helper to ensure presigned S3 URLs send Content-Disposition: inline rather than forcing attachment download
  const getPreviewUrl = (rawUrl?: string) => {
    if (!rawUrl) return '';
    return rawUrl
      .replace(/response-content-disposition=attachment/gi, 'response-content-disposition=inline')
      .replace(/response-content-disposition=attachment%3B/gi, 'response-content-disposition=inline%3B');
  };

  // Fallback checks from filename or URL extension if type is missing
  const getIsImage = (f: MediaFile) =>
    isImage(f.type) ||
    (f.name && f.name.match(/\.(jpeg|jpg|gif|png|webp|svg)$/i) !== null) ||
    (f.url && f.url.split('?')[0].match(/\.(jpeg|jpg|gif|png|webp|svg)$/i) !== null);

  const getIsVideo = (f: MediaFile) =>
    isVideo(f.type) ||
    (f.name && f.name.match(/\.(mp4|mov|avi|webm)$/i) !== null) ||
    (f.url && f.url.split('?')[0].match(/\.(mp4|mov|avi|webm)$/i) !== null);

  const getIsPdf = (f: MediaFile) =>
    isPdf(f.type) ||
    (f.name && f.name.match(/\.(pdf)$/i) !== null) ||
    (f.url && f.url.split('?')[0].match(/\.(pdf)$/i) !== null);

  const getIsDoc = (f: MediaFile) =>
    isDoc(f.type) ||
    (f.name && f.name.match(/\.(doc|docx)$/i) !== null) ||
    (f.url && f.url.split('?')[0].match(/\.(doc|docx)$/i) !== null);

  const handleDownload = () => {
    if (!file) return;
    if (onDownload) {
      onDownload(file);
    } else if (file.url) {
      window.open(file.url, '_blank');
    }
  };

  const previewUrl = file ? getPreviewUrl(file.url) : '';

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-[100]">
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="mx-auto max-w-5xl w-full h-[90vh] bg-primary-variant rounded-xl border border-primary-subtle flex flex-col overflow-hidden shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-primary-subtle bg-primary">
            <div className="flex items-center gap-3 min-w-0 pr-4">
              {file && getIsImage(file) ? (
                <ImageIcon className="w-5 h-5 text-teal-500 shrink-0" />
              ) : file && getIsVideo(file) ? (
                <VideoIcon className="w-5 h-5 text-purple-400 shrink-0" />
              ) : (
                <FileText className="w-5 h-5 text-blue-500 shrink-0" />
              )}
              <DialogTitle className="text-sm font-semibold text-neutral truncate">
                {file?.name}
              </DialogTitle>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              {file && (
                <button
                  type="button"
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-3 py-1.5 bg-[#1E2230] border border-[#2A2F3D] hover:bg-[#2A2F3D] text-white text-xs font-medium rounded-lg transition-colors"
                  title="Download File"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Download</span>
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="p-2 text-neutral-medium hover:text-neutral hover:bg-primary-subtle rounded-lg transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto bg-[#0a0c10] flex items-center justify-center p-4">
            {isFetchingUrl ? (
              <div className="text-center">
                <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
                <p className="text-neutral-medium">Fetching preview...</p>
              </div>
            ) : file && getIsImage(file) ? (
              <img
                src={previewUrl}
                alt={file.name}
                className="max-w-full max-h-full object-contain rounded-sm"
              />
            ) : file && getIsVideo(file) ? (
              <video
                src={previewUrl}
                controls
                className="max-w-full max-h-full rounded-sm"
              >
                Your browser can't play this video format.
              </video>
            ) : file && getIsPdf(file) ? (
              <iframe
                src={`${previewUrl}#toolbar=0`}
                className="w-full h-full rounded-sm bg-white border-0"
                title={file.name}
              />
            ) : file && getIsDoc(file) ? (
              <iframe
                src={`https://docs.google.com/gview?url=${encodeURIComponent(previewUrl)}&embedded=true`}
                className="w-full h-full rounded-sm bg-white border-0"
                title={file.name}
              />
            ) : (
              <div className="text-center">
                <FileText className="w-16 h-16 text-neutral-medium mx-auto mb-4 opacity-20" />
                <p className="text-neutral-medium mb-6">Preview not available for this file type.</p>
                <button
                  onClick={handleDownload}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all"
                >
                  <Download className="w-4 h-4" />
                  Download File
                </button>
              </div>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default MediaViewerModal;
